import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Mail, Phone, MapPin, Clock, Send, Instagram, Facebook, Twitter, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { trackLead } from "../utils/metaTracking";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BASE_URL = process.env.REACT_APP_BACKEND_URL;

// Helper to get full image URL
const getImageUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('http')) return url;
  if (url.startsWith('/uploads')) return `${BASE_URL}/api${url}`;
  return url;
};

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    subject: "",
    message: ""
  });
  const [submitting, setSubmitting] = useState(false);
  const [siteSettings, setSiteSettings] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const response = await axios.get(`${API}/site-settings`);
        setSiteSettings(response.data);
      } catch (error) {
        console.error("Error fetching settings:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchSettings();
  }, []);

  // Get contact page content from CMS or use defaults
  const contactPage = siteSettings?.contact_page || {};
  
  const pageContent = {
    title: contactPage.title || "Get in Touch",
    subtitle: contactPage.subtitle || "Ready to elevate your next event?",
    description: contactPage.description || "Let's create something extraordinary together. Reach out to discuss your vision, and we'll craft an unforgettable experience for you and your guests.",
    form_title: contactPage.form_title || "Send Us a Message",
    hero_image: contactPage.hero_image || "https://images.unsplash.com/photo-1551024709-8f23befc6f87?w=800"
  };

  const whatsappRaw = contactPage.whatsapp || siteSettings?.footer?.whatsapp || "";
  const whatsappDigits = String(whatsappRaw).replace(/[^0-9]/g, "");

  const contactInfo = [
    {
      icon: Phone,
      title: "Phone",
      value: contactPage.phone || siteSettings?.footer?.phone || "(780) 555-0123",
      link: `tel:${(contactPage.phone || siteSettings?.footer?.phone || "(780) 555-0123").replace(/[^0-9+]/g, '')}`
    },
    {
      icon: Mail,
      title: "Email",
      value: contactPage.email || siteSettings?.footer?.email || "hello@mixandmingle.ca",
      link: `mailto:${contactPage.email || siteSettings?.footer?.email || "hello@mixandmingle.ca"}`
    },
    ...(whatsappDigits
      ? [{
          icon: MessageCircle,
          title: "WhatsApp",
          value: "Chat on WhatsApp",
          link: `https://wa.me/${whatsappDigits}`,
          external: true,
        }]
      : []),
    {
      icon: MapPin,
      title: "Location",
      value: contactPage.address || siteSettings?.footer?.address || "Edmonton, Alberta, Canada",
      link: null
    },
    {
      icon: Clock,
      title: "Hours",
      value: contactPage.hours || "Mon-Fri: 9am-6pm, Sat: 10am-4pm",
      link: null
    }
  ];

  // Map settings from CMS
  const mapSettings = {
    enabled: contactPage.map_enabled !== false,
    embed_url: contactPage.map_embed_url || "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2370.0!2d-113.4909!3d53.5461!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zNTPCsDMyJzQ2LjAiTiAxMTPCsDI5JzI3LjIiVw!5e0!3m2!1sen!2sca!4v1234567890",
    height: contactPage.map_height || "400"
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.subject || !formData.message) {
      toast.error("Please fill in all required fields");
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, formData);
      toast.success("Message sent successfully! We'll get back to you soon.");
      // Meta: Lead on successful form submission
      trackLead({
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        source: 'contact_form',
      });
      setFormData({
        name: "",
        email: "",
        phone: "",
        subject: "",
        message: ""
      });
    } catch (error) {
      console.error("Error sending message:", error);
      toast.error("Failed to send message. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const fadeInUp = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0 }
  };

  return (
    <main className="pt-20" data-testid="contact-page">
      {/* Hero Section */}
      <section className="relative py-20 md:py-32 bg-[#050505]">
        <div className="absolute inset-0 opacity-20">
          <img 
            src={getImageUrl(pageContent.hero_image)}
            alt="Contact"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#050505] via-[#050505]/80 to-[#050505]" />
        </div>
        
        <div className="container-luxury relative z-10">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={fadeInUp}
            transition={{ duration: 0.6 }}
            className="text-center max-w-3xl mx-auto"
          >
            <span className="inline-block bg-[#D4AF37] text-[#050505] px-4 py-1 text-xs uppercase tracking-widest font-semibold mb-6">
              Contact Us
            </span>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-['Playfair_Display'] font-bold mb-6 text-[#F5F5F0]">
              {pageContent.title}
            </h1>
            <p className="text-[#D4AF37] text-xl mb-4">
              {pageContent.subtitle}
            </p>
            <p className="text-[#A1A1AA] text-lg leading-relaxed">
              {pageContent.description}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Contact Info Cards */}
      <section className="section-padding bg-[#0F0F0F]">
        <div className="container-luxury">
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
            {contactInfo.map((info, index) => (
              <motion.div
                key={info.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="luxury-card p-6 text-center"
              >
                <div className="w-12 h-12 border border-[#D4AF37] flex items-center justify-center mx-auto mb-4">
                  <info.icon className="w-5 h-5 text-[#D4AF37]" />
                </div>
                <h3 className="text-[#D4AF37] text-sm uppercase tracking-widest mb-2">
                  {info.title}
                </h3>
                {info.link ? (
                  <a 
                    href={info.link}
                    target={info.external ? "_blank" : undefined}
                    rel={info.external ? "noopener noreferrer" : undefined}
                    className="text-[#F5F5F0] hover:text-[#D4AF37] transition-colors"
                    data-testid={`contact-info-${info.title.toLowerCase()}`}
                  >
                    {info.value}
                  </a>
                ) : (
                  <p className="text-[#A1A1AA]">{info.value}</p>
                )}
              </motion.div>
            ))}
          </div>

          <div className="grid lg:grid-cols-2 gap-16">
            {/* Contact Form */}
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeInUp}
              transition={{ duration: 0.6 }}
            >
              <h2 className="text-2xl font-['Playfair_Display'] font-semibold text-[#F5F5F0] mb-6">
                {pageContent.form_title}
              </h2>
              <div className="gold-divider mb-8" />
              
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="form-label">Name *</label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      className="w-full bg-[#121212] border border-[rgba(212,175,55,0.3)] text-[#F5F5F0] p-4 focus:border-[#D4AF37] focus:outline-none"
                      required
                      data-testid="contact-name"
                    />
                  </div>
                  <div>
                    <label className="form-label">Email *</label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      className="w-full bg-[#121212] border border-[rgba(212,175,55,0.3)] text-[#F5F5F0] p-4 focus:border-[#D4AF37] focus:outline-none"
                      required
                      data-testid="contact-email"
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="form-label">Phone</label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      className="w-full bg-[#121212] border border-[rgba(212,175,55,0.3)] text-[#F5F5F0] p-4 focus:border-[#D4AF37] focus:outline-none"
                      data-testid="contact-phone"
                    />
                  </div>
                  <div>
                    <label className="form-label">Subject *</label>
                    <select
                      name="subject"
                      value={formData.subject}
                      onChange={handleInputChange}
                      className="w-full bg-[#121212] border border-[rgba(212,175,55,0.3)] text-[#F5F5F0] p-4 focus:border-[#D4AF37] focus:outline-none"
                      required
                      data-testid="contact-subject"
                    >
                      <option value="">Select a subject</option>
                      <option value="Event Inquiry">Event Inquiry</option>
                      <option value="Booking Question">Booking Question</option>
                      <option value="Partnership">Partnership</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="form-label">Message *</label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    rows={6}
                    className="w-full bg-[#121212] border border-[rgba(212,175,55,0.3)] text-[#F5F5F0] p-4 focus:border-[#D4AF37] focus:outline-none resize-none"
                    required
                    data-testid="contact-message"
                  />
                </div>

                <button
                  type="submit"
                  disabled={submitting}
                  className="btn-primary w-full flex items-center justify-center gap-2"
                  data-testid="contact-submit"
                >
                  {submitting ? (
                    <>
                      <div className="w-5 h-5 border-2 border-[#050505] border-t-transparent rounded-full animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <Send className="w-5 h-5" />
                      Send Message
                    </>
                  )}
                </button>
              </form>
            </motion.div>

            {/* Map */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <h2 className="text-2xl font-['Playfair_Display'] font-semibold text-[#F5F5F0] mb-6">
                Find Us
              </h2>
              <div className="gold-divider mb-8" />
              
              {mapSettings.enabled && mapSettings.embed_url ? (
                <div className="w-full overflow-hidden border border-[rgba(212,175,55,0.2)]">
                  <iframe
                    src={mapSettings.embed_url}
                    width="100%"
                    height={mapSettings.height}
                    style={{ border: 0 }}
                    allowFullScreen=""
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    title="Location Map"
                    data-testid="contact-map"
                  />
                </div>
              ) : (
                <div 
                  className="w-full bg-[#121212] border border-[rgba(212,175,55,0.2)] flex items-center justify-center"
                  style={{ height: mapSettings.height + 'px' }}
                >
                  <div className="text-center">
                    <MapPin className="w-12 h-12 text-[#D4AF37] mx-auto mb-4" />
                    <p className="text-[#A1A1AA]">{contactInfo.find(i => i.title === "Location")?.value}</p>
                  </div>
                </div>
              )}

              {/* Social Links */}
              {(siteSettings?.social || contactPage.social_links) && (
                <div className="mt-8">
                  <h3 className="text-[#D4AF37] text-sm uppercase tracking-widest mb-4">
                    Follow Us
                  </h3>
                  <div className="flex gap-4">
                    {(contactPage.social_links?.instagram || siteSettings?.social?.instagram) && (
                      <a 
                        href={contactPage.social_links?.instagram || siteSettings?.social?.instagram}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="w-10 h-10 border border-[#D4AF37] flex items-center justify-center text-[#D4AF37] hover:bg-[#D4AF37] hover:text-[#050505] transition-colors"
                      >
                        <Instagram className="w-5 h-5" />
                      </a>
                    )}
                    {(contactPage.social_links?.facebook || siteSettings?.social?.facebook) && (
                      <a 
                        href={contactPage.social_links?.facebook || siteSettings?.social?.facebook}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="w-10 h-10 border border-[#D4AF37] flex items-center justify-center text-[#D4AF37] hover:bg-[#D4AF37] hover:text-[#050505] transition-colors"
                      >
                        <Facebook className="w-5 h-5" />
                      </a>
                    )}
                    {(contactPage.social_links?.twitter || siteSettings?.social?.twitter) && (
                      <a 
                        href={contactPage.social_links?.twitter || siteSettings?.social?.twitter}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="w-10 h-10 border border-[#D4AF37] flex items-center justify-center text-[#D4AF37] hover:bg-[#D4AF37] hover:text-[#050505] transition-colors"
                      >
                        <Twitter className="w-5 h-5" />
                      </a>
                    )}
                  </div>
                </div>
              )}
            </motion.div>
          </div>
        </div>
      </section>
    </main>
  );
};

export default ContactPage;
